import React, { createContext, useContext, useState, useEffect } from 'react';

const BlockedUsersContext = createContext();

export const useBlockedUsers = () => {
  const context = useContext(BlockedUsersContext);
  if (!context) {
    throw new Error('useBlockedUsers must be used within a BlockedUsersProvider');
  }
  return context;
};

// Configuration - adjust these to match your IPFS setup
const IPFS_SERVER_URL = process.env.REACT_APP_IPFS_SERVER_URL || 'https://serenity.ing/api/v0';
const IPFS_GATEWAY_URL = process.env.REACT_APP_IPFS_GATEWAY_URL || 'https://serenity.ing/ipfs';
const USE_IPFS_STORAGE = process.env.REACT_APP_USE_IPFS_BLOCKLIST === 'true';

export const BlockedUsersProvider = ({ children }) => {
  const [blockedUsers, setBlockedUsers] = useState(new Set());
  const [ipfsHash, setIpfsHash] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load blocked users on startup
  useEffect(() => {
    loadBlockedUsers();
  }, []);

  const loadBlockedUsers = async () => {
    setLoading(true);
    try {
      if (USE_IPFS_STORAGE) {
        await loadFromIPFS();
      } else {
        await loadFromLocalStorage();
      }
    } catch (error) {
      console.error('Error loading blocked users:', error);
      // Fallback to localStorage if IPFS fails
      await loadFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  const loadFromIPFS = async () => {
    try {
      // Get current IPFS hash from localStorage or you could store it in your smart contract
      const currentHash = localStorage.getItem('blockedUsersIPFSHash');
      
      if (currentHash) {
        console.log('Loading blocked users from IPFS:', currentHash);
        const downloadUrl = `${IPFS_GATEWAY_URL}/${currentHash}`;
        console.log('Download URL:', downloadUrl);
        
        const response = await fetch(downloadUrl);
        console.log('Download response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Downloaded data:', data);
          
          // Validate the data structure
          if (data.version && Array.isArray(data.blockedUsers)) {
            setBlockedUsers(new Set(data.blockedUsers.map(addr => addr.toLowerCase())));
            setIpfsHash(currentHash);
            console.log(`Loaded ${data.blockedUsers.length} blocked users from IPFS`);
            return;
          } else {
            console.warn('Invalid data structure from IPFS:', data);
          }
        } else {
          const errorText = await response.text();
          console.error('Failed to download from IPFS:', response.status, errorText);
        }
      }
      
      // If no hash or failed to load, check localStorage as fallback
      console.log('No IPFS hash found or failed to load, checking localStorage');
      await loadFromLocalStorage();
      
    } catch (error) {
      console.warn('Failed to load from IPFS:', error);
      await loadFromLocalStorage();
    }
  };

  const loadFromLocalStorage = async () => {
    const stored = localStorage.getItem('blockedUsers');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setBlockedUsers(new Set(parsed.map(addr => addr.toLowerCase())));
        console.log(`Loaded ${parsed.length} blocked users from localStorage`);
      } catch (error) {
        console.error('Error parsing localStorage blocked users:', error);
      }
    }
  };

  const saveBlockedUsers = async (updatedUsers) => {
    try {
      if (USE_IPFS_STORAGE) {
        await saveToIPFS(updatedUsers);
      } else {
        await saveToLocalStorage(updatedUsers);
      }
    } catch (error) {
      console.error('Error saving blocked users:', error);
      // Always fallback to localStorage
      await saveToLocalStorage(updatedUsers);
    }
  };

  const saveToIPFS = async (updatedUsers) => {
    try {
      const blocklistData = {
        version: 1,
        timestamp: Date.now(),
        blockedUsers: Array.from(updatedUsers),
        description: 'Carbon Credit Marketplace Blocked Users List',
        // You could add admin signature here for additional security
        // adminSignature: await signBlocklist(updatedUsers)
      };

      console.log('Uploading blocked users list to IPFS...');
      console.log('IPFS Server URL:', IPFS_SERVER_URL);
      console.log('Data to upload:', blocklistData);
      
      // Create form data for IPFS upload
      const formData = new FormData();
      const jsonBlob = new Blob([JSON.stringify(blocklistData, null, 2)], {
        type: 'application/json'
      });
      formData.append('file', jsonBlob, 'blocked-users.json');

      // Upload to your IPFS server
      const uploadUrl = `${IPFS_SERVER_URL}/add?pin=true`;
      console.log('Upload URL:', uploadUrl);
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
        // Add headers that might be needed
        headers: {
          // Don't set Content-Type for FormData, let browser set it with boundary
        }
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', [...response.headers.entries()]);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('IPFS upload error response:', errorText);
        throw new Error(`IPFS upload failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('IPFS upload result:', result);
      
      const newHash = result.Hash;
      
      if (!newHash) {
        throw new Error('No hash returned from IPFS upload');
      }
      
      console.log('Successfully uploaded to IPFS:', newHash);
      
      // Store hash for future reference
      localStorage.setItem('blockedUsersIPFSHash', newHash);
      setIpfsHash(newHash);
      
      // Also save to localStorage as backup
      await saveToLocalStorage(updatedUsers);
      
      // TODO: Optionally store hash in smart contract for fully decentralized reference
      // await storeHashInContract(newHash);
      
    } catch (error) {
      console.error('Failed to save to IPFS:', error);
      throw error; // Re-throw to trigger localStorage fallback
    }
  };

  const saveToLocalStorage = async (updatedUsers) => {
    const blockedArray = Array.from(updatedUsers);
    localStorage.setItem('blockedUsers', JSON.stringify(blockedArray));
    console.log('Saved to localStorage:', blockedArray.length, 'users');
  };

  const blockUser = (address) => {
    const normalizedAddress = address.toLowerCase();
    const updatedUsers = new Set([...blockedUsers, normalizedAddress]);
    setBlockedUsers(updatedUsers);
    
    // Save asynchronously
    saveBlockedUsers(updatedUsers).catch(console.error);
  };

  const unblockUser = (address) => {
    const normalizedAddress = address.toLowerCase();
    const updatedUsers = new Set(blockedUsers);
    updatedUsers.delete(normalizedAddress);
    setBlockedUsers(updatedUsers);
    
    // Save asynchronously
    saveBlockedUsers(updatedUsers).catch(console.error);
  };

  const isUserBlocked = (address) => {
    return blockedUsers.has(address?.toLowerCase());
  };

  const toggleUserBlock = (address) => {
    if (isUserBlocked(address)) {
      unblockUser(address);
      return false; // Now unblocked
    } else {
      blockUser(address);
      return true; // Now blocked
    }
  };

  const getBlockedUsersList = () => {
    return Array.from(blockedUsers);
  };

  const refreshFromIPFS = async () => {
    if (USE_IPFS_STORAGE) {
      await loadFromIPFS();
    }
  };

  const getIPFSInfo = () => {
    return {
      enabled: USE_IPFS_STORAGE,
      currentHash: ipfsHash,
      gatewayUrl: IPFS_GATEWAY_URL,
      serverUrl: IPFS_SERVER_URL
    };
  };

  const testIPFSConnection = async () => {
    try {
      console.log('Testing IPFS connection...');
      console.log('Server URL:', IPFS_SERVER_URL);
      console.log('Gateway URL:', IPFS_GATEWAY_URL);
      
      // Create a simple test file
      const testData = {
        test: true,
        timestamp: Date.now(),
        message: 'IPFS connection test'
      };
      
      const formData = new FormData();
      const jsonBlob = new Blob([JSON.stringify(testData, null, 2)], {
        type: 'application/json'
      });
      formData.append('file', jsonBlob, 'test.json');

      // Upload test file
      const uploadUrl = `${IPFS_SERVER_URL}/add?pin=true`;
      console.log('Testing upload to:', uploadUrl);
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });

      console.log('Upload response status:', uploadResponse.status);
      
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`Upload failed: ${uploadResponse.status} - ${errorText}`);
      }

      const uploadResult = await uploadResponse.json();
      console.log('Upload result:', uploadResult);
      
      const testHash = uploadResult.Hash;
      
      // Test download
      const downloadUrl = `${IPFS_GATEWAY_URL}/${testHash}`;
      console.log('Testing download from:', downloadUrl);
      
      const downloadResponse = await fetch(downloadUrl);
      console.log('Download response status:', downloadResponse.status);
      
      if (!downloadResponse.ok) {
        const errorText = await downloadResponse.text();
        throw new Error(`Download failed: ${downloadResponse.status} - ${errorText}`);
      }

      const downloadResult = await downloadResponse.json();
      console.log('Download result:', downloadResult);
      
      if (downloadResult.test && downloadResult.message === 'IPFS connection test') {
        console.log('✅ IPFS connection test successful!');
        return { success: true, hash: testHash, data: downloadResult };
      } else {
        throw new Error('Downloaded data does not match uploaded data');
      }
      
    } catch (error) {
      console.error('❌ IPFS connection test failed:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    blockedUsers,
    blockUser,
    unblockUser,
    isUserBlocked,
    toggleUserBlock,
    getBlockedUsersList,
    refreshFromIPFS,
    getIPFSInfo,
    testIPFSConnection,
    loading
  };

  return (
    <BlockedUsersContext.Provider value={value}>
      {children}
    </BlockedUsersContext.Provider>
  );
};

export default BlockedUsersProvider;
